import * as Sentry from '@sentry/react';
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { SCHOOLS_PATH, studentsPath, USER_REGISTRATION_TIMESTAMP_CONFIG_KEY, userConfigPath, userEmailVerificationPath, userLangPath, userPackagesTemp, userProfilePath, userSchoolsPath } from '../../functions/src/admin/paths';
import { PACKAGE_STATUS_ORDERED, FULFILLMENT_RETRY_BUDGET_MS } from '../../functions/src/api/constants';
import firebase, { currentDb, DataSnapshot, functions, schoolDatabase } from '../api/firebase';
import { useDatabase, useDatabaseVal, useFirebaseUser, useList } from '../api/firebaseHooks';
import { getFirebaseCallableErrorCode, invokeCallableWithRetry, isCallableTransportError } from '../api/callableRetry';
import { isBasketFulfillmentRetryable, isFulfillmentTransientError, PACKAGE_PAY_PENDING_STORAGE_KEY, placeOrderPackage, useOrderCheckoutSession } from '../api/funds';
import { useServerNowMs } from '../api/useServerNow';
import { readPackagePaymentReturnOnLoad, resolvePlaceOrderPackageId, shouldRunBasketFulfillmentOnPaySuccess, shouldRunPackageFulfillmentOnPaySuccess } from './vivaPaymentReturn';
import { useClearStalePackageTemps } from './userPackages/useClearStalePackageTemps';
import BasketProvider, { useBasketContext } from './basket/BasketProvider';
import { useBasketStrings } from './basket/basket-strings';
import { getLangValue, langContext, Language, resolveSchoolLang, useLanguageProvider, useSchoolLangs } from '../api/Lang';
import LoadingSpinner from '../api/LoadingSpinner';
import OkDlg from '../api/OkDlg';
import { captureException, setUserContext } from '../api/sentry';
import { APP_VERSION, BUILD_TIMESTAMP } from '../api/version';
import YesNoDlg from '../api/YesNoDlg';
import { useStrings } from '../common-strings';
import CookiesConsent from '../components/CookiesConsent';
import Login from '../Login/Login';
import MaintenanceScreen from './MaintenanceScreen';
import { Schools, Student, UserConfig, UserProfile } from '../types';
import Feedback from './Feedback';
import PlatformSupport from './support/PlatformSupport';
import type { PlatformSupportLeaveGuard } from './support/platformSupportLeaveGuard';
import { usePlatformSupportStrings } from './platform-support-strings';
import { Footer } from './layout/Footer';
import { Header } from './layout/Header';
import GefsinusNavPanel from './layout/GefsinusNavPanel';
import Navigator, { NavigatorOption } from './layout/Navigator';
import Order from './Order';
import './order-app.css';
import Settings from './profile/Settings';
import UserProfileComp from './profile/UserProfileComp';
import { getProfileTabFromMenu, isBasketPaymentSuccessHash, isProfileMenu, parseAppHash, profileHash, resolveVisibleProfileTab, type ProfileTab } from './profile/profileTabs';
import MonthlyMenu from './userPackages/MonthlyMenu';
import News from './news/News';
import Packages from './userPackages/Packages';
import { getOrderAppStrings, getProfErrorDlgMsg, useOrderAppStrings } from './order-app-strings';
import { useTopupStrings } from './topup-strings';
import { useMaintenanceMode } from './useMaintenanceMode';
import { scrollOrderAppContentToTop } from './scrollOrderAppContent';
import { checkProfile, PROFILE_VALIDATION_SUCCESS } from './validation';
const NAV_OPTIONS: NavigatorOption[] = ['order', 'profile', 'packages', 'monthlyMenu', 'news', 'feedback', 'settings', 'platformSupport', 'paySuccess', 'payFail'];

export default function OrderApp() {
    const user = useFirebaseUser();
    const lang = useLanguageProvider();
    const { langs: applicableLangs, defaultLang: schoolDefaultLang, loaded: schoolLangsLoaded } = useSchoolLangs();
    const orderAppStringsAuth = getOrderAppStrings(lang[0]);
    const maintenanceMode = useMaintenanceMode(lang[0]);
    const [defaultLangInitialized, setDefaultLangInitialized] = useState(false);
    const [cookiesConsent, setCookiesConsent] = useState<boolean | null>(null);

    // This effect runs once to check for existing cookie consent.
    useEffect(() => {
        const hasConsent = localStorage.getItem('cookiesConsent');
        if (hasConsent) {
            const accepted = hasConsent === 'accepted';
            setCookiesConsent(accepted);
            if (accepted) {
                // Initialize Sentry if consent was previously given
                import('../api/sentry').then(({ initSentry }) => {
                    initSentry();
                }).catch((err) => console.warn('Sentry init failed:', err));
            }
        }
    }, []);

    // This effect handles the language initialization for non-logged-in users.
    useEffect(() => {
        // Only proceed when user state is determined (not undefined)
        if (user === undefined) {
            return; // Wait for Firebase auth to finish loading
        }

        if (user === null && !defaultLangInitialized && !localStorage.getItem('lang')) {
            if (!schoolLangsLoaded) {
                return;
            }
            const resolved = resolveSchoolLang(schoolDefaultLang, applicableLangs, schoolDefaultLang ?? applicableLangs[0]);
            lang[1](resolved);
            localStorage.setItem('lang', resolved);
            setDefaultLangInitialized(true);
        } else if (user !== null || localStorage.getItem('lang')) {
            // User is logged in or language preference exists — still clamp below once school langs load
            setDefaultLangInitialized(true);
        }
    }, [user, defaultLangInitialized, lang, schoolLangsLoaded, applicableLangs, schoolDefaultLang]);

    // Keep the active UI language within this school's applicable langs (incl. stale localStorage on login).
    useEffect(() => {
        if (!schoolLangsLoaded || user === undefined) {
            return;
        }
        const resolved = resolveSchoolLang(lang[0], applicableLangs, schoolDefaultLang ?? applicableLangs[0]);
        if (resolved !== lang[0]) {
            lang[1](resolved);
        }
    }, [schoolLangsLoaded, applicableLangs, schoolDefaultLang, lang, user]);

    // Callback for the CookiesConsent component
    const handleCookiesConsent = (accepted: boolean) => {
        setCookiesConsent(accepted);
        if (accepted) {
            // Initialize Sentry only after consent is given
            import('../api/sentry').then(({ initSentry }) => {
                initSentry();
            }).catch((err) => console.warn('Sentry init failed:', err));
        }
    };

    return (
        <langContext.Provider value={lang}>
            {cookiesConsent === null && maintenanceMode.status !== "active" && <CookiesConsent onConsent={handleCookiesConsent} />}
            {(() => {
                if (maintenanceMode.status === "loading") {
                    return <div>{orderAppStringsAuth.authLoadingPleaseWait}</div>;
                }
                if (maintenanceMode.status === "active") {
                    return <MaintenanceScreen message={maintenanceMode.message} />;
                }
                if (user === undefined) {
                    return <div>{orderAppStringsAuth.authLoadingPleaseWait}</div>;
                }
                if (!user?.uid) {
                    return <Login />;
                }
                return <BasketProvider><LoggedApp user={user} /></BasketProvider>;
            })()}
        </langContext.Provider>
    );
}

function LoggedApp({ user }: { user: firebase.User }) {

    const { uid } = user;
    const [lang, setLang] = useContext(langContext);
    const commonStrings = useStrings();
    const orderAppStrings = useOrderAppStrings();
    const topupStrings = useTopupStrings();
    const isWideOrderLayout = useMediaQuery({ minWidth: 931 });

    // Kick the school database connection to prevent 120s zombie state
    useEffect(() => {
        const timer = setTimeout(() => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
                return;
            }
            schoolDatabase.goOffline();
            schoolDatabase.goOnline();
        }, 1000);
        return () => clearTimeout(timer);
    }, []);

    // Package Viva return: processing / success / error (never show success until placeOrderPackage succeeds)
    const [packageFulfillment, setPackageFulfillment] = useState<'idle' | 'processing' | 'done' | 'error'>('idle');
    const [packageTitle, setPackageTitle] = useState('');
    const [packageErrorMsg, setPackageErrorMsg] = useState<string | null>(null);
    const [packageSuccessMsg, setPackageSuccessMsg] = useState<string | null>(null);
    const packagePayHandledRef = useRef(false);

    // Dynamic header height adjustment
    useEffect(() => {
        const updateHeaderHeight = () => {
            const header = document.querySelector('#orderApp > .header') as HTMLElement;
            if (header) {
                const headerHeight = header.offsetHeight;
                document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
            }
        };

        // Update on mount and resize
        updateHeaderHeight();
        window.addEventListener('resize', updateHeaderHeight);

        // Listen for image load events that might affect header height
        const handleImageLoad = () => {
            // Small delay to ensure the image has fully rendered
            setTimeout(updateHeaderHeight, 50);
        };

        // Add listeners to all images in the header
        const headerImages = document.querySelectorAll('#orderApp > .header img');
        headerImages.forEach(img => {
            img.addEventListener('load', handleImageLoad);
            img.addEventListener('error', handleImageLoad); // Also handle error cases
        });

        // Update when language changes (could affect header height)
        const timeoutId = setTimeout(updateHeaderHeight, 100);

        return () => {
            window.removeEventListener('resize', updateHeaderHeight);
            clearTimeout(timeoutId);

            // Remove image listeners
            const headerImages = document.querySelectorAll('#orderApp > .header img');
            headerImages.forEach(img => {
                img.removeEventListener('load', handleImageLoad);
                img.removeEventListener('error', handleImageLoad);
            });
        };
    }, [lang]); // Re-run when language changes

    // Set user context for Sentry
    useEffect(() => {
        setUserContext({
            id: uid,
            email: user.email || undefined,
        });
    }, [uid, user.email]);

    const dbLang: Language = useDatabase(userLangPath(uid))?.val();
    useEffect(() => {
        if (!dbLang || lang != dbLang)
            return setLang(lang);
    }, [lang]);

    const students = useDatabase(studentsPath(uid));
    const studentsList: DataSnapshot<Student>[] = useList(students);
    // useList returns [] before the snapshot loads — treat that as still loading, not "no people"
    const studentsForValidation = students === undefined ? undefined : studentsList;
    const path = userProfilePath(uid);
    const [profile, sProfile] = useDatabaseVal<UserProfile>(path);

    // Get email verification status from database
    const [emailVerificationData] = useDatabaseVal(userEmailVerificationPath(uid));

    const profileOK = useMemo(() => checkProfile(profile, studentsForValidation, emailVerificationData), [profile, studentsForValidation, emailVerificationData]);

    const [dlgProfError, setDlgProfError] = useState(0);
    const [profileValidationScrollRequest, setProfileValidationScrollRequest] = useState(0);
    const dlgErrMsg = typeof dlgProfError === 'number' ? getProfErrorDlgMsg(dlgProfError, orderAppStrings) : orderAppStrings.profileErrorUnknown;

    const initialHash = parseAppHash(window.location.hash);

    const [menu, setMenuState] = useState<NavigatorOption>(() => {
        const { menu: parsedMenu } = initialHash;

        // Viva return: show Order behind basket/package registration overlays (not Account / lastMenu)
        if (isBasketPaymentSuccessHash(window.location.hash)) {
            return 'order';
        }

        // If there's a valid hash in URL, use it (highest priority)
        if (isProfileMenu(parsedMenu) || NAV_OPTIONS.includes(parsedMenu as NavigatorOption)) {
            return isProfileMenu(parsedMenu) ? 'profile' : parsedMenu as NavigatorOption;
        }

        // Otherwise, try to get last menu from localStorage
        const lastMenu = localStorage.getItem('lastMenu') as NavigatorOption;
        if (lastMenu && NAV_OPTIONS.includes(lastMenu)) {
            return lastMenu;
        }

        // For new users, default to 'profile' instead of 'order'
        // This will be overridden by the useEffect below if profile is complete
        return 'profile';
    });

    const [profileTab, setProfileTab] = useState<ProfileTab>(() => initialHash.profileTab);
    const menuRef = useRef(menu);
    menuRef.current = menu;
    const platformSupportLeaveGuardRef = useRef<PlatformSupportLeaveGuard | null>(null);
    const [platformSupportLeavePending, setPlatformSupportLeavePending] = useState<{
        menu: NavigatorOption;
        shouldScrollToTop: boolean;
        tab?: ProfileTab;
    } | null>(null);
    const platformSupportStrings = usePlatformSupportStrings();

    // Track if we've already processed initial menu selection (URL hash counts as an explicit choice)
    const [initialMenuProcessed, setInitialMenuProcessed] = useState(() => {
        const { menu: parsedMenu } = parseAppHash(window.location.hash);
        return isProfileMenu(parsedMenu) || NAV_OPTIONS.includes(parsedMenu as NavigatorOption);
    });

    // When user logs in, set current school from the domain to his list of schools
    const userSchools = useDatabase(userSchoolsPath(uid));
    const allSchools: DataSnapshot<Schools[]> = useDatabase(SCHOOLS_PATH);
    const allSchoolsList: DataSnapshot<Schools>[] = useList(allSchools);
    const domainSchoolKey = useMemo(() => allSchoolsList?.filter(school => school.val().schoolDb === currentDb.databaseURL)[0]?.key, [allSchoolsList]);

    const [userConfig] = useDatabaseVal<UserConfig>(userConfigPath(uid));
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    // Check if there's a validation error for this school before auto-adding it
    const schoolGroupError = userConfig?.schoolGroupError;
    // Prevent adding school if it's in the invalid schools list, regardless of error age
    const hasValidationError = schoolGroupError &&
        schoolGroupError.invalidSchools?.includes(domainSchoolKey || '');

    useEffect(() => {
        // Only add school if it doesn't exist, there's no validation error for it, and we're not logging out
        if (domainSchoolKey && userSchools && !hasValidationError && !isLoggingOut) {
            const userSchoolsData = userSchools.val();
            if (!userSchoolsData || !userSchoolsData[domainSchoolKey]) {
                void userSchools.ref.child(domainSchoolKey).set(true).catch((err) => {
                    captureException(err instanceof Error ? err : new Error(String(err)), {
                        tags: { component: 'OrderApp', operation: 'addDomainSchoolToUserSchools' },
                        extra: { domainSchoolKey, uid },
                    });
                });
            }
        }
    }, [userSchools, domainSchoolKey, hasValidationError, isLoggingOut]);

    // Handle package completion after successful payment
    const userPackagesTempDb = useDatabase(userPackagesTemp(uid) || null);

    // Ordered packages can only exist in temp packages folder
    const orderedPackages = useList(userPackagesTempDb).filter(l => l.child('status').val() === PACKAGE_STATUS_ORDERED);

    // Multi-day basket checkout: pending session + drawer/overlay controls
    const checkoutSession = useOrderCheckoutSession();
    const basket = useBasketContext();
    const basketStrings = useBasketStrings();
    const basketPayHandledRef = useRef(false);
    /** Set once on load from #paySuccess — survives setMenu('order') clearing the hash. */
    const basketPaymentReturnRef = useRef(isBasketPaymentSuccessHash(window.location.hash));
    /**
     * Package Viva return flag (sessionStorage set in createOrderPayment). Survives server-side
     * fulfillment clearing ORDERED temps before the client listener updates.
     */
    const packagePaymentReturnRef = useRef(
        readPackagePaymentReturnOnLoad(window.location.hash, sessionStorage, PACKAGE_PAY_PENDING_STORAGE_KEY),
    );
    const packagesResolved = userPackagesTempDb !== undefined;
    /** Set in sessionStorage when redirecting to Viva for `orderPackage` only — not from stale packagesTemp rows. */
    const isPackagePaymentReturn = !!packagePaymentReturnRef.current;
    const serverNowMs = useServerNowMs();
    const noopStalePackageTempsCleared = useCallback(() => undefined, []);
    useClearStalePackageTemps(userPackagesTempDb, noopStalePackageTempsCleared, serverNowMs);

    const clearPackagePayPendingFlag = () => {
        packagePaymentReturnRef.current = null;
        try {
            sessionStorage.removeItem(PACKAGE_PAY_PENDING_STORAGE_KEY);
        } catch {
            // ignore
        }
    };

    // Land on Order immediately after Viva (basket or package) — do not wait for checkout session / package temp DB.
    useLayoutEffect(() => {
        if (!basketPaymentReturnRef.current) {
            return;
        }
        setMenuState('order');
        if (window.location.hash !== '#order') {
            window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#order`);
        }
    }, []);

    useEffect(() => {
        if (!basketPaymentReturnRef.current) {
            return;
        }
        setMenu('order', false);
    }, []);

    // Basket checkout overlay can start from session recovery without #paySuccess — still show Order behind it.
    useEffect(() => {
        if (!basket.fulfillment || menu === 'order') return;
        setMenu('order', false);
    }, [basket.fulfillment, menu]);

    // #paySuccess after basket Viva payment → confirm registration (server also fulfills in vivaSuccess/webhook)
    useEffect(() => {
        if (!shouldRunBasketFulfillmentOnPaySuccess({
            isPaySuccessReturn: basketPaymentReturnRef.current,
            basketPayHandled: basketPayHandledRef.current,
            checkoutSessionKnown: checkoutSession !== undefined,
            packagePaymentPendingId: packagePaymentReturnRef.current,
        })) {
            return;
        }

        basketPayHandledRef.current = true;
        basket.setBasketLocked(false);
        // Always treat #paySuccess as a paid Viva return for basket — session may already be cleared server-side.
        void basket.runFulfillment(true);
    }, [checkoutSession, basket]);

    // #payFail with a pending basket session → back to the basket with an inline error (State 1)
    useEffect(() => {
        if (!menu.includes('payFail') || basketPayHandledRef.current || checkoutSession === undefined) return;
        if (checkoutSession && !packagePaymentReturnRef.current) {
            basketPayHandledRef.current = true;
            basket.setBasketLocked(false);
            basket.setBasketError(basketStrings.vivaFailedTryAgain);
            basket.openBasket();
            setMenu('order');
        }
    }, [menu, checkoutSession]);

    // Basket "Edit day" while on another tab — Order.tsx only mounts on the order tab
    useEffect(() => {
        if (!basket.focusOrderDate || menu === 'order') return;
        setMenu('order', false);
    }, [basket.focusOrderDate, menu]);

    // Allow a later checkout in the same session to be handled again
    useEffect(() => {
        if (!basketPaymentReturnRef.current && menu !== 'paySuccess' && !menu.includes('payFail')) {
            basketPayHandledRef.current = false;
        }
    }, [menu]);

    const configInitializedRef = React.useRef<string | null>(null);
    const profileEmailFromAuthSyncedRef = React.useRef<string | null>(null);
    const profileEmailFromAuthInFlightRef = React.useRef<string | null>(null);
    const configSyncInFlightRef = React.useRef<string | null>(null);
    const [showSchoolGroupError, setShowSchoolGroupError] = useState(false);

    useEffect(() => {
        if (schoolGroupError && schoolGroupError.timestamp) {
            // Check if current school is in the invalid schools list            
            if (domainSchoolKey && schoolGroupError.invalidSchools?.includes(domainSchoolKey)) {
                // Current school is invalid - always show dialog and log out, regardless of error age
                if (!showSchoolGroupError && !isLoggingOut) {
                    setShowSchoolGroupError(true);
                }
            }
            // Note: Stale errors (where current school is valid) are handled by backend
            // Backend will clear them when user successfully logs in to a valid school
        }
    }, [schoolGroupError, showSchoolGroupError, uid, domainSchoolKey, userSchools, isLoggingOut]);

    const handleSchoolGroupErrorClose = async () => {
        setShowSchoolGroupError(false);

        // Always log user out when closing this error dialog
        // The error indicates the user tried to access an invalid school
        // Backend will clear the error when appropriate (e.g., on logout or next valid login)
        setIsLoggingOut(true);
        try {
            // User will stay on login page to create a new account for this school
            await firebase.auth().signOut();
        } catch (error) {
            console.error('Failed to sign out:', error);
            setIsLoggingOut(false);
        }
    };

    // Email/password signup only sets Auth email — copy into profile/email on first order-app load.
    useEffect(() => {
        if (!uid || !user) {
            return;
        }
        if (profile === undefined) {
            return;
        }

        const profileEmail = (profile?.email ?? '').trim();
        const authEmail = (user.email ?? '').trim();
        if (!authEmail || profileEmail) {
            profileEmailFromAuthSyncedRef.current = uid;
            return;
        }
        if (profileEmailFromAuthSyncedRef.current === uid || profileEmailFromAuthInFlightRef.current === uid) {
            return;
        }

        profileEmailFromAuthInFlightRef.current = uid;
        (async () => {
            try {
                const ensureProfileEmailFromAuth = functions.httpsCallable('ensureProfileEmailFromAuth');
                await invokeCallableWithRetry(() => ensureProfileEmailFromAuth());
                profileEmailFromAuthSyncedRef.current = uid;
            } catch (error) {
                if (isCallableTransportError(error)) {
                    console.warn('ensureProfileEmailFromAuth failed (transient transport, best-effort):', error);
                } else {
                    console.warn('ensureProfileEmailFromAuth failed (best-effort):', error);
                }
            } finally {
                if (profileEmailFromAuthInFlightRef.current === uid) {
                    profileEmailFromAuthInFlightRef.current = null;
                }
            }
        })();
    }, [uid, user, profile]);

    // Handle user config: registration timestamp, build number, version, Brevo status
    // Only run once per user when profile and user first become available.
    // Callables use short retries for transient errors; Brevo marketing sync is best-effort (no Sentry).
    useEffect(() => {
        // Skip if already initialized for this user
        if (configInitializedRef.current === uid || configSyncInFlightRef.current === uid) {
            return;
        }

        if (!profile || !user) {
            // Wait for profile and user to load
            return;
        }

        // Wait for userConfig to load (it might be undefined while loading)
        if (userConfig === undefined) {
            return;
        }

        // Wait for userSchools and allSchoolsList to load before creating Brevo contact
        // This ensures we have school data when creating the contact
        if (!userSchools || allSchoolsList?.length === 0) {
            return;
        }

        // Wait for domain school to be set in userSchools (the useEffect at line 273 sets it)
        // We need this before creating Brevo contact to include school tags
        const domainSchoolKey = allSchoolsList.find(school => school.val().schoolDb === currentDb.databaseURL)?.key;
        if (domainSchoolKey) {
            const userSchoolsData = userSchools.val();
            if (!userSchoolsData || !userSchoolsData[domainSchoolKey]) {
                return;
            }
        }

        const currentConfig = userConfig || {};
        const marketingEmail = profile.email?.trim() || user.email?.trim() || '';

        // Always get current build and version
        const buildNumber = process.env.SENTRY_RELEASE || BUILD_TIMESTAMP.substring(0, 13).replace(/[-:T]/g, ''); // Use BUILD_TIMESTAMP as fallback

        const needsRegistration = !currentConfig.registration?.timestamp;
        const needsBrevo = !currentConfig.brevo?.timestamp;
        const needsAppUpdate =
            currentConfig.app?.build !== buildNumber ||
            currentConfig.app?.version !== APP_VERSION;

        const needsConfigUpdate = needsRegistration || needsBrevo || needsAppUpdate;

        // Skip if config is complete and up-to-date
        if (!needsConfigUpdate) {
            configInitializedRef.current = uid;
            return;
        }

        // Registration / app version can be written without email; Brevo sync needs an email.
        const canProceed = needsRegistration || needsAppUpdate || (needsBrevo && !!marketingEmail);
        if (!canProceed) {
            return;
        }

        configSyncInFlightRef.current = uid;

        (async () => {
            try {
                const updates: { [key: string]: any } = {};

                // Always store current build number and version number
                updates['app/build'] = buildNumber;
                updates['app/version'] = APP_VERSION;

                // Check if registration timestamp exists, if not get from Firebase Auth
                if (needsRegistration) {
                    const registrationTimestamp = user.metadata.creationTime
                        ? new Date(user.metadata.creationTime).getTime()
                        : Date.now();
                    updates[USER_REGISTRATION_TIMESTAMP_CONFIG_KEY] = registrationTimestamp;
                }

                // Check if Brevo timestamp exists
                if (needsBrevo && marketingEmail) {
                    // Check if user exists in Brevo
                    const checkBrevoContact = functions.httpsCallable('checkBrevoContactStatus');
                    try {
                        const brevoStatus = await invokeCallableWithRetry(() => checkBrevoContact());
                        const brevoData = brevoStatus.data as { exists: boolean; timestamp?: number };

                        if (brevoData.exists) {
                            // User exists in Brevo - store timestamp
                            updates['brevo/timestamp'] = brevoData.timestamp || Date.now();
                        } else {
                            // User doesn't exist in Brevo - create contact
                            const createBrevoContact = functions.httpsCallable('createBrevoContactOnRegistration');
                            const firstName = profile.firstName || '';
                            const lastName = profile.lastName || '';
                            const language = lang || 'en';

                            // Get school tags from user's schools
                            const schoolTags: string[] = [];
                            if (userSchools && allSchoolsList && allSchoolsList.length > 0) {
                                const schools = allSchoolsList.reduce((acc, school) => {
                                    acc[school.key] = school.val();
                                    return acc;
                                }, {} as { [key: string]: Schools });

                                const userSchoolsData = userSchools.val();
                                if (schools && userSchoolsData) {
                                    Object.keys(userSchoolsData).forEach((schoolKey) => {
                                        if (schools[schoolKey]?.mcTag) {
                                            schoolTags.push(schools[schoolKey].mcTag);
                                        }
                                    });
                                }
                            }
                            // If no school tags, contact will be created without them
                            // The updateBrevoContactSchoolTags trigger will update it later when schools are assigned
                            try {
                                await invokeCallableWithRetry(() =>
                                    createBrevoContact({
                                        firstName,
                                        lastName,
                                        language,
                                        schoolTags
                                    })
                                );
                                // Store timestamp after successful creation
                                updates['brevo/timestamp'] = Date.now();
                            } catch (error) {
                                console.warn('createBrevoContactOnRegistration failed (best-effort):', error);
                            }
                        }
                    } catch (error) {
                        console.warn('checkBrevoContactStatus failed (best-effort):', error);
                    }
                }

                // Apply all updates at once if any were made - call function instead of direct write
                if (Object.keys(updates).length > 0) {
                    const updateUserConfig = functions.httpsCallable('updateUserConfig');
                    await invokeCallableWithRetry(() => updateUserConfig({ updates }));
                }

                // Mark as initialized for this user after successful update
                configInitializedRef.current = uid;
            } catch (error) {
                if (isCallableTransportError(error)) {
                    console.warn('updateUserConfig failed (transient transport, best-effort):', error);
                } else {
                    captureException(error as Error, {
                        contexts: {
                            userConfig: {
                                uid,
                                error: (error as Error).message
                            }
                        },
                        tags: {
                            component: 'OrderApp',
                            operation: 'updateUserConfig',
                            ...((c) => (c ? { firebase_code: c } : {}))(getFirebaseCallableErrorCode(error)),
                        }
                    });
                }
            } finally {
                if (configSyncInFlightRef.current === uid) {
                    configSyncInFlightRef.current = null;
                }
            }
        })();
        // Run when uid/profile/user/userConfig/allSchoolsList/userSchools first become available (ref prevents re-running after initialization)
        // Note: userConfig, allSchoolsList, and userSchools are included to detect when they load
        // Note: lang is used but not in dependencies
    }, [uid, profile, user, userConfig, allSchoolsList, userSchools]);

    const goToPersonalInfoTab = (shouldScrollToTop: boolean = true, scrollToValidationIssue: boolean = false) => {
        window.location.hash = profileHash('personal');
        setMenuState('profile');
        setProfileTab('personal');
        localStorage.setItem('lastMenu', 'profile');
        if (scrollToValidationIssue) {
            setProfileValidationScrollRequest((n) => n + 1);
        }
        if (shouldScrollToTop) {
            scrollOrderAppContentToTop();
        }
    };

    const commitMenuNavigation = (menu: NavigatorOption, shouldScrollToTop: boolean = true, tab?: ProfileTab) => {
        const rawTab = tab ?? (isProfileMenu(menu) ? getProfileTabFromMenu(menu) : profileTab);
        const resolvedTab = resolveVisibleProfileTab(rawTab);
        const isProfile = menu === 'profile' || isProfileMenu(menu);
        const hash = isProfile ? profileHash(resolvedTab) : menu;

        window.location.hash = hash;
        setMenuState(isProfile ? 'profile' : menu);
        if (isProfile) {
            setProfileTab(resolvedTab);
        }

        // Save the menu choice to localStorage for persistence
        // don't save temporary states like paySuccess/payFail
        if (menu !== 'paySuccess' && !menu.includes('payFail')) {
            localStorage.setItem('lastMenu', isProfile ? 'profile' : menu);
        }

        // Mark that we've processed the initial menu selection
        if (!initialMenuProcessed) {
            setInitialMenuProcessed(true);
        }

        // Scroll to top if requested
        if (shouldScrollToTop) {
            scrollOrderAppContentToTop();
        }
    };

    const setMenu = (menu: NavigatorOption, shouldScrollToTop: boolean = true, tab?: ProfileTab) => {
        // Only enforce profile validation if there's an actual validation error (not loading)
        if (typeof profileOK === 'number' && profileOK !== PROFILE_VALIDATION_SUCCESS && (menu === 'order' || menu === 'packages')) {
            setDlgProfError(profileOK);
            return;
        }

        const isProfileTarget = menu === 'profile' || isProfileMenu(menu);
        const targetMenu = isProfileTarget ? 'profile' : menu;
        if (
            menuRef.current === 'platformSupport'
            && targetMenu !== 'platformSupport'
            && platformSupportLeaveGuardRef.current?.hasDraftContent()
        ) {
            setPlatformSupportLeavePending({ menu, shouldScrollToTop, tab });
            return;
        }

        commitMenuNavigation(menu, shouldScrollToTop, tab);
    };

    const setProfileTabNav = (tab: ProfileTab) => {
        setMenu('profile', true, tab);
    };

    useEffect(() => {
        const onHashChange = () => {
            const parsed = parseAppHash(window.location.hash);
            const isProfileHash = isProfileMenu(parsed.menu) || parsed.menu === 'profile';
            const nextMenu: NavigatorOption = isProfileHash
                ? 'profile'
                : (NAV_OPTIONS.includes(parsed.menu as NavigatorOption) ? parsed.menu as NavigatorOption : menuRef.current);

            if (
                menuRef.current === 'platformSupport'
                && nextMenu !== 'platformSupport'
                && platformSupportLeaveGuardRef.current?.hasDraftContent()
            ) {
                window.location.hash = 'platformSupport';
                setPlatformSupportLeavePending({
                    menu: nextMenu,
                    shouldScrollToTop: true,
                    tab: isProfileHash ? resolveVisibleProfileTab(parsed.profileTab) : undefined,
                });
                return;
            }

            if (isProfileHash) {
                setMenuState('profile');
                setProfileTab(resolveVisibleProfileTab(parsed.profileTab));
                return;
            }
            if (NAV_OPTIONS.includes(parsed.menu as NavigatorOption)) {
                setMenuState(parsed.menu as NavigatorOption);
            }
        };
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, []);

    useEffect(() => {
        if (profileOK === PROFILE_VALIDATION_SUCCESS) {
            setDlgProfError(PROFILE_VALIDATION_SUCCESS);
        }
    }, [profileOK]);

    useEffect(() => {
        // Incomplete profile: block order/packages until personal info is fixed (do not auto-leave account when profile becomes complete)
        if (typeof profileOK === 'number' && profileOK !== PROFILE_VALIDATION_SUCCESS) {
            const currentMenu = menuRef.current;
            if (currentMenu === 'order' || currentMenu === 'packages') {
                goToPersonalInfoTab(true, true);
            }
        }
    }, [profileOK]);

    useEffect(() => {
        if (dlgProfError !== PROFILE_VALIDATION_SUCCESS && menu === 'profile' && profileTab !== 'personal') {
            goToPersonalInfoTab(false, true);
        }
    }, [dlgProfError, menu, profileTab]);

    // Handle package completion after Viva return (server also fulfills in vivaSuccess/webhook)
    useEffect(() => {
        if (!shouldRunPackageFulfillmentOnPaySuccess({
            isPaySuccessReturn: basketPaymentReturnRef.current,
            packagesResolved,
            packagePayHandled: packagePayHandledRef.current,
            packagePaymentPendingId: packagePaymentReturnRef.current,
        })) {
            return;
        }

        packagePayHandledRef.current = true;
        setPackageFulfillment('processing');

        const packageSnapshot = orderedPackages?.[0];
        const packageId = packagePaymentReturnRef.current
            ? resolvePlaceOrderPackageId({
                packagePaymentPendingId: packagePaymentReturnRef.current,
                snapshotPackageId: packageSnapshot?.child('packageId').val(),
            })
            : null;
        const packageTitleRaw = packageSnapshot?.child('title').val();
        const resolvedTitle = packageTitleRaw && typeof packageTitleRaw === 'object'
            ? (getLangValue(packageTitleRaw, lang) || orderAppStrings.packageGenericTitle)
            : (packageTitleRaw || orderAppStrings.packageGenericTitle);
        const successTitle = resolvedTitle + orderAppStrings.packageTitleSuffix;
        const packagePrice = packageSnapshot?.child('price').val() || 0;

        if (!packageId) {
            setPackageErrorMsg(topupStrings.packageOrderCompletionFailed);
            setPackageFulfillment('error');
            clearPackagePayPendingFlag();
            return;
        }

        (async () => {
            const retryDeadline = Date.now() + FULFILLMENT_RETRY_BUDGET_MS;
            const retryDelayMs = 2000;
            const userDate = Date.now();

            for (; ;) {
                try {
                    const packageResult = await placeOrderPackage({
                        packageId,
                        userDate,
                        price: packagePrice,
                        localDbURL: currentDb.databaseURL
                    });
                    if (packageResult.error) {
                        throw new Error(packageResult.error);
                    }
                    setPackageSuccessMsg(
                        packageResult.copyComplete === false
                            ? (packageResult.message || orderAppStrings.packageCopyFinishingByUs)
                            : orderAppStrings.packageRegisteredBody,
                    );
                    setPackageTitle(successTitle);
                    setPackageFulfillment('done');
                    clearPackagePayPendingFlag();
                    return;
                } catch (error) {
                    const shouldRetry = Date.now() + retryDelayMs < retryDeadline && isBasketFulfillmentRetryable(error, true);
                    if (shouldRetry) {
                        await new Promise(resolve => setTimeout(resolve, retryDelayMs));
                        continue;
                    }
                    console.error('Failed to complete package order:', error);
                    if (!isCallableTransportError(error) && !isFulfillmentTransientError(error)) {
                        Sentry.captureException(error);
                    }
                    setPackageErrorMsg(error instanceof Error ? error.message : topupStrings.packageOrderCompletionFailed);
                    setPackageFulfillment('error');
                    clearPackagePayPendingFlag();
                    return;
                }
            }
        })();
    }, [orderedPackages, packagesResolved, lang, orderAppStrings, topupStrings]);

    // Reset package fulfillment UI when leaving the payment-return flow (menu is order during Viva return)
    useEffect(() => {
        if (basketPaymentReturnRef.current || packagePaymentReturnRef.current) {
            return;
        }
        if (menu !== 'paySuccess') {
            setPackageTitle('');
            setPackageErrorMsg(null);
            setPackageSuccessMsg(null);
            setPackageFulfillment('idle');
            packagePayHandledRef.current = false;
        }
    }, [menu]);

    const packageVivaOverlay = (checkoutSession === undefined || !packagesResolved)
        ? null
        : (packageFulfillment === 'processing' || (packageFulfillment === 'idle' && isPackagePaymentReturn && basketPaymentReturnRef.current))
            ? <LoadingSpinner />
            : packageFulfillment === 'error'
                ? (
                    <OkDlg
                        title={orderAppStrings.packageRegisterFailedTitle}
                        msg={packageErrorMsg || topupStrings.packageOrderCompletionFailed}
                        onClose={() => {
                            clearPackagePayPendingFlag();
                            basketPaymentReturnRef.current = false;
                            setMenu('packages');
                        }}
                    />
                )
                : packageFulfillment === 'done'
                    ? (
                        <OkDlg
                            title={packageTitle || (orderAppStrings.packageGenericTitle + orderAppStrings.packageTitleSuffix)}
                            msg={packageSuccessMsg || orderAppStrings.packageRegisteredBody}
                            onClose={() => {
                                clearPackagePayPendingFlag();
                                basketPaymentReturnRef.current = false;
                                setMenu('order');
                            }}
                        />
                    )
                    : null;

    return (
        <div id="orderApp">
            {platformSupportLeavePending && (
                <YesNoDlg
                    title={platformSupportStrings.leaveDraftTitle}
                    msg={platformSupportStrings.leaveDraftMsg}
                    yes={platformSupportStrings.leaveDraftYes}
                    no={commonStrings.cancel}
                    onNo={() => setPlatformSupportLeavePending(null)}
                    onYes={() => {
                        const pending = platformSupportLeavePending;
                        setPlatformSupportLeavePending(null);
                        if (pending) {
                            commitMenuNavigation(pending.menu, pending.shouldScrollToTop, pending.tab);
                        }
                    }}
                />
            )}
            <Header
                onRefill={() => { setMenu('profile', true, 'transactions') }}
                onNavigationChange={setMenu}
                currentSelection={menu}
            />

            <div className="main-content">
                <div className="navigator-column">
                    <Navigator selection={menu} onChange={setMenu} />
                    {isWideOrderLayout && <GefsinusNavPanel />}
                </div>

                <div className="content-column">
                    <div className='content'>{(() => {
                        // Show loading while profile is loading
                        if (profile === undefined) {
                            return <><div>{orderAppStrings.profileLoadingPleaseWait}</div><LoadingSpinner /></>;
                        }

                        // Show school group error dialog (highest priority)
                        if (showSchoolGroupError) {
                            return <OkDlg title={orderAppStrings.schoolGroupErrorTitle} msg={orderAppStrings.schoolGroupErrorMessage} onClose={handleSchoolGroupErrorClose} />;
                        }

                        // Show profile error dialog
                        if (dlgProfError !== PROFILE_VALIDATION_SUCCESS) {
                            if (menu === 'profile') {
                                return <OkDlg title={orderAppStrings.profileDlgErrorTitle} msg={dlgErrMsg} onClose={() => { setDlgProfError(PROFILE_VALIDATION_SUCCESS); goToPersonalInfoTab(false, true); }} />;
                            }
                            return <YesNoDlg title={orderAppStrings.profileDlgErrorTitle} msg={orderAppStrings.profileIncompleteNavigatePrompt} yes={commonStrings.goToProfile} no={commonStrings.ok} onYes={() => { goToPersonalInfoTab(true, true); setDlgProfError(PROFILE_VALIDATION_SUCCESS); }} onNo={() => setDlgProfError(PROFILE_VALIDATION_SUCCESS)} />;
                        }

                        // Show the selected menu content
                        switch (menu) {
                            case 'profile':
                                return <UserProfileComp profSnapshot={sProfile} students={students} profileTab={profileTab} onProfileTabChange={setProfileTabNav} profileValidationScrollRequest={profileValidationScrollRequest} onFinish={() => {
                                    // Only redirect to order if user explicitly came from navigation, not from auto-redirect
                                    const lastMenu = localStorage.getItem('lastMenu') as NavigatorOption;
                                    if (lastMenu && NAV_OPTIONS.includes(lastMenu) && lastMenu !== 'profile') {
                                        setMenu(lastMenu);
                                    } else {
                                        setMenu('order');
                                    }
                                }} />;
                            case 'settings':
                                return <Settings onOpenFeedback={() => setMenu('feedback')} />;
                            case 'order':
                                return <Order setMenu={setMenu} />;
                            case 'packages':
                                return <Packages />;
                            case 'monthlyMenu':
                                return <MonthlyMenu />;
                            case 'news':
                                return <News />;
                            case 'feedback':
                                return <Feedback onFinish={() => { setMenu('settings') }} />;
                            case 'platformSupport':
                                return <PlatformSupport profile={profile} leaveGuardRef={platformSupportLeaveGuardRef} />;
                            case 'paySuccess':
                                // Legacy hash; Viva return now lands on `order` — BasketPaymentResult / packageVivaOverlay sit on top
                                return <Order setMenu={setMenu} />;
                            default:
                                if (menu.includes('payFail')) {
                                    // Basket checkout return: the effect above reopens the basket with an inline error
                                    if (checkoutSession === undefined || (checkoutSession && !packagesResolved)) {
                                        return <LoadingSpinner />;
                                    }
                                    if (checkoutSession && !isPackagePaymentReturn) {
                                        return <Order setMenu={setMenu} />;
                                    }
                                    return <OkDlg title={orderAppStrings.paymentFailTitle} msg={orderAppStrings.paymentFailMsg} onClose={() => setMenu('profile', true, 'personal')} />;
                                }
                                return <Order setMenu={setMenu} />; // fallback
                        }
                    })()}</div>
                    {isWideOrderLayout && <Footer onOpenPlatformSupport={() => setMenu('platformSupport')} />}
                </div>

                <div className="sidebar-column">
                    {/* Future sidebar content */}
                </div>
            </div>

            {!isWideOrderLayout && <Footer onOpenPlatformSupport={() => setMenu('platformSupport')} />}
            {packageVivaOverlay}
        </div>
    );
}